import React, { useState } from 'react';
import { Button } from '../../../components/Button';
import { Input } from '../../../components/Input';
import { Modal } from '../../../components/Modal';
import type { TabItem } from '../../../types';

interface AddItemModalProps {
    isOpen: boolean;
    onClose: () => void;
    onAdd: (name: string, price: number, quantity: number, category: 'Food' | 'Drink' | 'Other') => Promise<void>;
    itemToEdit?: TabItem;
    onEdit?: (id: string, updates: { name: string; price: number; quantity: number; category: 'Food' | 'Drink' | 'Other' }) => Promise<void>;
    onDelete?: (id: string) => void;
}

// Inner component that re-mounts when the key changes (add vs edit, or different item)
const AddItemModalContent: React.FC<AddItemModalProps & { isEditing: boolean }> = ({
    isOpen,
    onClose,
    onAdd,
    itemToEdit,
    onEdit,
    onDelete,
    isEditing,
}) => {
    const [name, setName] = useState(itemToEdit?.name ?? '');
    const [price, setPrice] = useState(itemToEdit ? itemToEdit.price.toString() : '');
    const [quantity, setQuantity] = useState(itemToEdit ? (itemToEdit.quantity ?? 1).toString() : '1');
    const [category, setCategory] = useState<'Food' | 'Drink' | 'Other'>(itemToEdit?.category ?? 'Food');

    const handleSubmit = async () => {
        if (!name || !price) return;
        const qty = parseInt(quantity) || 1;
        const priceNum = parseFloat(price);

        if (isEditing && itemToEdit && onEdit) {
            await onEdit(itemToEdit.id, { name, price: priceNum, quantity: qty, category });
        } else {
            await onAdd(name, priceNum, qty, category);
        }
        onClose();
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} title={isEditing ? 'Edit Item' : 'Add Item to Bill'}>
            <div className="space-y-4">
                <Input
                    label="Item Name"
                    placeholder="e.g. Margherita Pizza"
                    value={name}
                    onChange={(e) => { setName(e.target.value); }}
                    autoFocus={isOpen}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') void handleSubmit();
                    }}
                />

                <div className="flex gap-3">
                    <div className="flex-1">
                        <Input
                            label="Price (each)"
                            type="number"
                            step="0.01"
                            placeholder="0.00"
                            value={price}
                            onChange={(e) => { setPrice(e.target.value); }}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    void handleSubmit();
                                }
                            }}
                        />
                    </div>
                    <div className="w-24">
                        <Input
                            label="Qty"
                            type="number"
                            step="1"
                            min="1"
                            placeholder="1"
                            value={quantity}
                            onChange={(e) => { setQuantity(e.target.value); }}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    void handleSubmit();
                                }
                            }}
                        />
                    </div>
                </div>

                {parseInt(quantity) > 1 && price && (
                    <p className="text-xs text-right text-text-muted font-medium">
                        Total: ${(parseFloat(price) * parseInt(quantity)).toFixed(2)}
                    </p>
                )}

                <div className="flex flex-col gap-1.5">
                    <label className="text-xs font-semibold text-text-muted uppercase tracking-wider">Category</label>
                    <div className="flex bg-bg-surface border border-border-default rounded-lg p-1">
                        {(['Food', 'Drink', 'Other'] as const).map((cat) => (
                            <button
                                key={cat}
                                type="button"
                                className={`flex-1 py-2 text-sm font-medium rounded-md transition-all ${category === cat
                                    ? 'bg-primary-base text-white shadow-sm'
                                    : 'text-text-muted hover:bg-bg-base'
                                    }`}
                                onClick={() => { setCategory(cat); }}
                            >
                                {cat}
                            </button>
                        ))}
                    </div>
                </div>
                {isEditing && onDelete ? (
                    <div className="flex gap-3 pt-2">
                        <Button
                            type="button"
                            variant="outline"
                            className="w-1/3 h-12 text-red-600 border-red-200 hover:bg-red-50 hover:border-red-300 dark:border-red-900/50 dark:hover:bg-red-900/20 dark:hover:text-red-500"
                            onClick={() => { if (itemToEdit) onDelete(itemToEdit.id); }}
                        >
                            Delete
                        </Button>
                        <Button
                            type="button"
                            className="flex-1 h-12"
                            onClick={handleSubmit}
                            disabled={!name || !price}
                        >
                            Save Changes
                        </Button>
                    </div>
                ) : (
                    <Button
                        type="button"
                        className="w-full h-12 pt-2"
                        onClick={handleSubmit}
                        disabled={!name || !price}
                    >
                        {isEditing ? 'Save Changes' : `Add ${parseInt(quantity) > 1 ? `${quantity} Items` : 'Item'}`}
                    </Button>
                )}
            </div>
        </Modal>
    );
};

// Outer wrapper uses key to force full re-mount when switching between add/edit modes
export const AddItemModal: React.FC<AddItemModalProps> = (props) => {
    const isEditing = !!props.itemToEdit;
    const key = props.itemToEdit ? `edit-${props.itemToEdit.id}` : 'add';
    return <AddItemModalContent key={key} {...props} isEditing={isEditing} />;
};
